import { isSameMonth, isToday } from 'date-fns';
import { getUserColor, formatTime12Hour, computeRemainingAvailability } from '@/utils/calendarHelpers';
import type { DayData } from '@/components/calendar/types';

interface MiniCalendarProps {
  title: string;
  days: DayData[];
  currentDate: Date;
  mode?: 'planned' | 'actual';
}

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function MiniCalendar({ title, days, currentDate, mode = 'planned' }: MiniCalendarProps) {
  return (
    <div>
      {title && (
        <h3 className="text-sm font-semibold text-gray-300 mb-2 text-center">
          {title}
        </h3>
      )}
      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAY_HEADERS.map((d, i) => (
          <div
            key={i}
            className="text-center text-[10px] font-medium text-gray-500"
          >
            {d}
          </div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7 gap-px">
        {days.map((day) => {
          const inMonth = isSameMonth(day.date, currentDate);
          const today = isToday(day.date);

          // Compute remaining availability windows (green unclaimed)
          const remainingWindows = computeRemainingAvailability(
            day.availabilityWindows,
            day.claimedSlots
          );

          const activeSlots = day.claimedSlots.filter((s: any) => s.status !== 'cancelled');

          // Items to show: remaining windows + claimed slots
          const windowItems = remainingWindows.slice(0, 2);
          const slotItems = activeSlots.slice(0, 2);
          const totalItems = remainingWindows.length + activeSlots.length;
          const shownItems = windowItems.length + slotItems.length;

          return (
            <div
              key={day.dateString}
              className={`flex flex-col min-h-[70px] sm:min-h-[100px] p-0.5 sm:p-1 rounded relative ${
                !inMonth ? 'opacity-30' : ''
              } ${today ? 'ring-1 ring-blue-400' : ''}`}
            >
              {/* Day number top-left, Today badge top-right */}
              <div className="flex items-start justify-between mb-0.5">
                <span
                  className={`text-[10px] sm:text-xs leading-tight font-medium ${
                    today
                      ? 'text-blue-400 font-bold'
                      : inMonth
                      ? 'text-gray-300'
                      : 'text-gray-600'
                  }`}
                >
                  {day.date.getDate()}
                </span>
                {today && (
                  <span className="text-[7px] sm:text-[8px] bg-blue-500 text-white px-0.5 rounded">
                    Today
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="space-y-0.5 overflow-hidden">
                {/* Remaining availability windows (green) */}
                {windowItems.map((w, idx) => (
                  <div
                    key={`avail-${idx}`}
                    className="text-[8px] sm:text-[9px] bg-emerald-500/20 text-emerald-400 px-0.5 py-px rounded truncate"
                  >
                    {formatTime12Hour(w.start)}-{formatTime12Hour(w.end)}
                  </div>
                ))}

                {/* Claimed slots with color, emoji, name */}
                {slotItems.map((slot: any, idx: number) => {
                  // In actual mode, apply status-based coloring
                  let statusIndicator = null;
                  let bgColor = `${getUserColor(slot.caretaker)}25`;
                  let borderColor = getUserColor(slot.caretaker);
                  let borderStyle = slot.is_default ? 'dashed' : 'solid';

                  if (mode === 'actual') {
                    // Compute actual status from slot fields
                    const actualStatus = slot.no_show
                      ? 'no_show'
                      : slot.clock_in_time && !slot.clock_out_time
                        ? 'in_progress'
                        : slot.status === 'completed'
                          ? 'completed'
                          : 'pending';

                    if (actualStatus === 'no_show') {
                      bgColor = 'rgba(239, 68, 68, 0.15)';
                      borderColor = '#ef4444';
                      statusIndicator = (
                        <span className="text-[7px] text-red-400 font-medium">NO SHOW</span>
                      );
                    } else if (actualStatus === 'in_progress') {
                      bgColor = 'rgba(59, 130, 246, 0.15)';
                      borderColor = '#3b82f6';
                      statusIndicator = (
                        <span className="text-[7px] text-blue-400 animate-pulse">LIVE</span>
                      );
                    } else if (actualStatus === 'completed') {
                      // Check if started late (actual start differs from planned start)
                      if (slot.actual_start_time && slot.actual_start_time !== slot.start_time) {
                        bgColor = 'rgba(245, 158, 11, 0.15)';
                        borderColor = '#f59e0b';
                        statusIndicator = (
                          <span className="text-[7px] text-amber-400">LATE</span>
                        );
                      } else {
                        bgColor = 'rgba(16, 185, 129, 0.15)';
                        borderColor = '#10b981';
                      }
                    }
                  }

                  return (
                    <div
                      key={`slot-${idx}`}
                      className="text-[8px] sm:text-[9px] px-0.5 py-px rounded truncate"
                      style={{
                        backgroundColor: bgColor,
                        borderLeft: `2px ${borderStyle} ${borderColor}`,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-[7px] sm:text-[8px]">
                          {formatTime12Hour(slot.start_time)}-{formatTime12Hour(slot.end_time)}
                        </span>
                        {statusIndicator}
                      </div>
                      <div className="flex items-center gap-0.5">
                        {slot.caretaker?.emoji && <span className="flex-shrink-0 text-[8px]">{slot.caretaker.emoji}</span>}
                        <span
                          className="truncate text-[8px] sm:text-[9px]"
                          style={{ color: mode === 'actual' && slot.no_show ? '#ef4444' : getUserColor(slot.caretaker, '#9ca3af') }}
                        >
                          {slot.caretaker?.first_name || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {/* Overflow indicator */}
                {totalItems > shownItems && (
                  <div className="text-[7px] sm:text-[8px] text-gray-500">
                    +{totalItems - shownItems} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
